// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useRef, useState } from 'react';
import openBankingApi from '../api/openBankingApi';
import type { ConnectionLifecycleStatus, UserStatus } from '../types/openBanking';

const FAILED: ConnectionLifecycleStatus[] = [
  'LoadingAccountsFailed',
  'LoadingTransactionsFailed',
  'LoadingAnalyticsFailed',
];

const TERMINAL: ConnectionLifecycleStatus[] = ['LoadingCompleted', ...FAILED];

const POLL_MS = 3000;

export interface UseBankConnectionStatusResult {
  status: UserStatus | null;
  /** True for the very first fetch only; goes false after the first response (success or error). */
  loading: boolean;
  error: Error | null;
  /** True while the server-side load is in progress (Refreshing / Loading*). */
  isPolling: boolean;
  /** True when accountStatus is LoadingCompleted. */
  isCompleted: boolean;
  /** True when accountStatus is one of the *Failed terminal states. */
  isFailed: boolean;
  /** True when no provider is registered for this user — the "empty / not connected" state. */
  noProvider: boolean;
}

/**
 * Polls {@code GET /openbanking/user/{userId}/status} every {@code POLL_MS}
 * milliseconds while the {@code accountStatus} is non-terminal (Refreshing
 * or any of the Loading* in-progress states), then stops. Terminal states
 * are {@code LoadingCompleted} and the three {@code *Failed} variants.
 *
 * <p>Passing a falsy {@code userId} is a no-op — the hook returns
 * {@code noProvider=true} so the page can render its empty state instead
 * of erroring on the missing federated id (clients invited but not yet
 * activated have no Keycloak id).
 */
export function useBankConnectionStatus(userId: string | null | undefined): UseBankConnectionStatusResult {
  const [status, setStatus] = useState<UserStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(userId));
  const [error, setError] = useState<Error | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!userId) {
      setStatus(null);
      setLoading(false);
      setError(null);
      return () => { cancelled.current = true; };
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const data = await openBankingApi.getStatus(userId);
        if (cancelled.current) return;
        setStatus(data);
        setError(null);
        setLoading(false);
        const isTerminal = TERMINAL.includes(data.accountStatus as ConnectionLifecycleStatus);
        if (!isTerminal) {
          timer = setTimeout(poll, POLL_MS);
        }
      } catch (e) {
        if (cancelled.current) return;
        // 404 from /status = no provider registered for this user. Surface as
        // an empty/no-provider state, not as an error — the user simply hasn't
        // connected a bank yet.
        if (isHttp404(e)) {
          setStatus(null);
          setError(null);
          setLoading(false);
          return;
        }
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
        // Stay quiet on error — don't keep hammering a broken endpoint.
      }
    };

    setLoading(true);
    poll();

    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [userId]);

  const accountStatus = status?.accountStatus as ConnectionLifecycleStatus | undefined;
  // No provider when userId is missing entirely OR when /status returned but
  // didn't carry a provider (404-as-no-provider, or a future contract where
  // provider can be null on a 200).
  const noProvider = !userId || (!loading && !error && (status === null || !status.provider));
  const isCompleted = accountStatus === 'LoadingCompleted';
  const isFailed = accountStatus !== undefined && FAILED.includes(accountStatus);
  const isPolling = !!accountStatus && !TERMINAL.includes(accountStatus);

  return { status, loading, error, isPolling, isCompleted, isFailed, noProvider };
}

function isHttp404(e: unknown): boolean {
  if (typeof e !== 'object' || e === null) return false;
  // Axios surface: error.response.status
  const response = (e as { response?: { status?: number } }).response;
  return response?.status === 404;
}
